import makeReducerFor from "./_genericDbReducer";
import { createSelector } from "reselect";
import { getFlowItemsForSelectedFlow } from "./flowItems";
import { notifyBlack } from "../actions/notifyActions";
import { getFlowCasesArray } from "../reducers/flowCases";
import { getFlowItemsArray } from "../reducers/flowItems";
import { getFlowFiltersArray } from "../reducers/flowFilters";
import {
    isValidParentChild,
    updateCacheIfStateChanged,
    getAncestorFieldsForFlowItems,
    getLoopCacheKey,
    isDirectChild,
    recursiveLoopCheck,
} from "./flowRelationHelpers";
import { validateRelationTypeRule, unifiedValidationRunner } from "./flowRelationValidationRules";

const myGenericReducer = makeReducerFor("FLOW_RELATION", "FlowRelationId");
import subItemReducer from "./_genericFlowSubItemReducer";
import { getFlowFiltersForSelectedFlow } from "./flowFilters";
import { getFlowCasesForSelectedFlow } from "./flowCases";
import { getOACLabelIds } from "./fieldLabels";
import { contains3PMFields } from "../helpers/fieldIsThirdParty";
import { getAncestorFieldIdsForFlowItemId } from "../helpers/flowItems";
import { fieldsHaveNeedsApprovalLabels } from "../helpers/needsApproval";

const myReducer = (state = {}, action) => subItemReducer(myGenericReducer(state, action), action);
export default myReducer;

///////// SELECTORS /////////////

export const getFlowRelationsArray = createSelector(
    state => state.flowRelations.byId,
    flowRelationsById => {
        const r = Object.values(flowRelationsById);
        return r;
    }
);

let loopLastStateSeen = { value: null };
let loopCachedAnswers = { value: {} };

// TODO: Make a version that uses the getFlowItemChildrenIndex instead of the entire state, so we can hook it up to FlowItemPort
// Helper function: Validate input parameters

// Refactored isLoopDetected function
export const isLoopDetected = (state, parentId, childId) => {
    if (!isValidParentChild(parentId, childId)) return false;
    updateCacheIfStateChanged(state, loopLastStateSeen, loopCachedAnswers);
    const cacheKey = getLoopCacheKey(parentId, childId);
    if (cacheKey in loopCachedAnswers.value) return loopCachedAnswers.value[cacheKey];
    const childrenIndex = getFlowItemChildrenIndex(state);
    const parentChildren = childrenIndex[parentId];
    // Early return if no children
    if (!parentChildren || parentChildren.length === 0) {
        loopCachedAnswers.value[cacheKey] = false;
        return false;
    }
    // Direct child check
    if (isDirectChild(parentChildren, childId)) {
        loopCachedAnswers.value[cacheKey] = true;
        return true;
    }
    // Recursive check
    const result = recursiveLoopCheck(state, parentChildren, childId, isLoopDetected);
    loopCachedAnswers.value[cacheKey] = result;
    return result;
};

/// Flow Relations Validations / Enforcement ///

// WE completely disallow empty here, because this is for user-generated links,
// and the system generates empties.
export const allowedToBeParentOfNewRelations = itemType =>
    !CANNOT_HAVE_CHILD_SET.has(itemType) && !CHILD_MUST_BE_EMPTY_SET.has(itemType);

export const allowedToBeChildOfNewRelations = (itemType, numParents) =>
    !CANNOT_HAVE_PARENT_SET.has(itemType) && (numParents == 0 || ALLOWS_MULTIPLE_PARENTS_SET.has(itemType));

// --- Consolidated validation rules config ---
// Moved to flowRelationValidationRules.js

// --- Generic validation rules config ---
// Moved to flowRelationValidationRules.js

// Replace isNewRelationValid to use unified validation runner
export const isNewRelationValid = function (
    childItemType,
    parentItemType,
    childNumParents,
    shouldSkipMultipleParentCheck,
    notifyOnFail = false,
    notifyFn = null
) {
    if (shouldSkipMultipleParentCheck === undefined) shouldSkipMultipleParentCheck = false;
    // Use unified validation runner
    const result = unifiedValidationRunner({
        parentType: parentItemType,
        childType: childItemType,
        childNumParents,
        skipMultiParentCheck: shouldSkipMultipleParentCheck,
        notifyOnFail,
        notifyFn,
    });
    if (!result.allowed) return result;
    // Also run generic relationTypeRules if needed (legacy support)
    const relTypeResult = validateRelationTypeRule(childItemType, parentItemType);
    if (relTypeResult && !relTypeResult.allowed) return relTypeResult;
    return { allowed: true, message: "" };
};

export const ancestorHas3PMData = function (state, flowItemId) {
    const ancestorFields = getAncestorsFields(state);
    const flowRelations = getFlowRelationsForSelectedFlow(state);
    if (!ancestorFields || !flowRelations) return false;
    const parents = [];
    getAncestorsFlowItemIds(flowRelations, flowItemId, parents);
    const parentAncestorFields = ancestorFields.filter(x => parents.includes(x.FlowItemId));
    return contains3PMFields(parentAncestorFields);
};

export const ancestorhasOACData = function (state, flowItemId) {
    const flowRelations = getFlowRelationsForSelectedFlow(state);
    const flowFilters = getFlowFiltersForSelectedFlow(state);
    const flowCases = getFlowCasesForSelectedFlow(state);
    const oacLabelIds = getOACLabelIds(state);
    const enabledFieldLabels = state.fieldsByCompany.enabledFieldLabels;
    const ancestorFieldIds = getAncestorFieldIdsForFlowItemId(flowItemId, flowRelations, flowFilters, flowCases);
    return fieldsHaveNeedsApprovalLabels(ancestorFieldIds, oacLabelIds, enabledFieldLabels, false);
};

// --- Lookup Sets for parent/child type checks ---
import {
    cannotHaveChild,
    cannotHaveParent,
    childMustBeEmpty,
    allowsMultipleParents,
} from "./flowRelationValidationRules";

const CANNOT_HAVE_CHILD_SET = new Set(cannotHaveChild);
const CANNOT_HAVE_PARENT_SET = new Set(cannotHaveParent);
const CHILD_MUST_BE_EMPTY_SET = new Set(childMustBeEmpty);
const ALLOWS_MULTIPLE_PARENTS_SET = new Set(allowsMultipleParents);

// --- Helper: Checks if a child type allows multiple parents ---
function isMultiParentAllowedType(childTypeName) {
    return ALLOWS_MULTIPLE_PARENTS_SET.has(childTypeName);
}

/**
 * Pure validation for a new flow relation. Returns an object:
 * { allowed: boolean, message: string }
 */
export function pureValidateFlowRelation(state, parentId, parentTypeName, childId, childTypeName) {
    // --- Helper: Validate parent/child relation ---
    function validateParentChild(parentId, childId) {
        return parentId !== childId && parentId != null && childId != null;
    }

    // --- Helper: Check if relation already exists ---
    function relationAlreadyExists(state, parentId, childId) {
        return flowRelationExists(state, parentId, childId);
    }

    // --- Helper: Multi-parent allowed logic ---
    function handleMultiParent(childTypeName, parentTypeName, childsNumberOfParents) {
        return validateMultiParentAllowed(childTypeName, parentTypeName, childsNumberOfParents);
    }

    if (!validateParentChild(parentId, childId)) {
        return { allowed: false, message: "Parent and child must be different and not null." };
    }
    const relationExists = relationAlreadyExists(state, parentId, childId);
    const childsNumberOfParents = existingFlowRelationsCount(state, null, childId);
    if (isMultiParentAllowedType(childTypeName)) {
        return handleMultiParent(childTypeName, parentTypeName, childsNumberOfParents);
    }
    if (childsNumberOfParents > 0 && !relationExists) {
        return {
            allowed: false,
            message: "Multiple parents are allowed for Merge, Model, Offer, expressions, and Script types only.",
        };
    }
    return handleMultiParent(childTypeName, parentTypeName, childsNumberOfParents);
}

// --- Helper: Validates multi-parent allowed types ---
function validateMultiParentAllowed(childTypeName, parentTypeName, childsNumberOfParents) {
    const allowedObj = isNewRelationValid(childTypeName, parentTypeName, childsNumberOfParents, true);
    if (!allowedObj.allowed) return allowedObj;
    return { allowed: true, message: "" };
}

/**
 * Calls pureValidateFlowRelation and handles notification side effects.
 * Returns true if valid, otherwise false.
 */
export function validateFlowRelation(state, parentId, parentTypeName, childId, childTypeName) {
    const result = pureValidateFlowRelation(state, parentId, parentTypeName, childId, childTypeName);
    if (!result.allowed) {
        if (typeof top?.store?.dispatch === "function") {
            top.store.dispatch(notifyBlack(result.message));
        }
        return false;
    }
    return true;
}

/**
 * Checks if a flow relation exists between parent and child.
 * Returns true if found, otherwise false.
 */
export const flowRelationExists = function (state, parentId, childId) {
    const flowRelations = getFlowRelationsForAllFlows(state);
    if (parentId != null && childId != null) {
        return flowRelations.filter(x => x.ParentFlowItemId == parentId && x.ChildFlowItemId == childId).length > 0;
    }
    if (childId != null) {
        return flowRelations.filter(x => x.ChildFlowItemId == childId).length > 0;
    }
    return false;
};

/**
 * Returns all existing relations matching the given parent and/or child.
 * If neither is provided, returns an empty array.
 */
export const getExistingRelations = function (flowRelations, parentId, childId) {
    if (!flowRelations) return [];
    if (parentId != null && childId != null) {
        return flowRelations.filter(x => x.ParentFlowItemId == parentId && x.ChildFlowItemId == childId);
    }
    if (childId != null) {
        // Early return for childId only
        return flowRelations.filter(x => x.ChildFlowItemId == childId && x.ParentFlowItemId != 0);
    }
    if (parentId != null) {
        // Early return for parentId only
        return flowRelations.filter(x => x.ParentFlowItemId == parentId);
    }
    return [];
};

export const existingFlowRelations = function (state, parentId, childId) {
    const flowRelations = getFlowRelationsForAllFlows(state);
    return getExistingRelations(flowRelations, parentId, childId);
};

export const existingFlowRelationsCount = function (state, parentId, childId) {
    const flowRelationsArray = existingFlowRelations(state, parentId, childId);
    return flowRelationsArray.length;
};

export const getFlowRelationsForAllFlows = getFlowRelationsArray;
export const getFlowRelationsForSelectedFlow = createSelector(
    state => getFlowItemsForSelectedFlow(state),
    state => getFlowRelationsArray(state),
    (flowItems, flowRelations) => {
        const getFlowItemIds = flowItems => flowItems.map(fi => fi.FlowItemId);
        const filterRelationsByItemIds = (flowRelations, itemIds) =>
            flowRelations.filter(fr => itemIds.includes(fr.ChildFlowItemId) || itemIds.includes(fr.ParentFlowItemId));
        const itemIds = getFlowItemIds(flowItems);
        return filterRelationsByItemIds(flowRelations, itemIds);
    }
);

// This shouldn't be deep equals, rather anything depending on it might want deep equals in its shouldcomponentupdate..
export const getFlowRelationIdsForSelectedFlow = createSelector(
    state => getFlowRelationsForSelectedFlow(state),
    flowRelations => flowRelations.map(x => x.FlowRelationId)
);

// Helper to build children index from flow relations
function buildChildrenIndex(flowRelations) {
    return flowRelations.reduce((acc, row) => {
        if (!acc[row.ParentFlowItemId]) {
            acc[row.ParentFlowItemId] = [];
        }
        acc[row.ParentFlowItemId].push(row.ChildFlowItemId);
        return acc;
    }, {});
}

export const getFlowItemChildrenIndex = createSelector(
    state => getFlowRelationsArray(state),
    flowRelations => buildChildrenIndex(flowRelations)
);

// Helper to get children for a selected flow item
function getChildrenForSelectedFlowItem(selectedFlowItem, flowItemChildrenIndex) {
    return flowItemChildrenIndex[selectedFlowItem] || [];
}

export const getFlowItemChildrenIndexForSelectedFlowItem = createSelector(
    state => state.selected.flowItem,
    state => getFlowItemChildrenIndex(state),
    (selectedFlowItem, flowItemChildrenIndex) => getChildrenForSelectedFlowItem(selectedFlowItem, flowItemChildrenIndex)
);

// Helper to build parent index from flow relations
function buildParentIndex(flowRelations) {
    return flowRelations.reduce((acc, row) => {
        if (!acc[row.ChildFlowItemId]) {
            acc[row.ChildFlowItemId] = [];
        }
        acc[row.ChildFlowItemId].push(row.ParentFlowItemId);
        return acc;
    }, {});
}

export const getFlowItemParentIndex = createSelector(
    state => getFlowRelationsArray(state),
    flowRelations => buildParentIndex(flowRelations)
);

export const getAncestorsFields = createSelector(
    state => getFlowRelationsForSelectedFlow(state),
    state => state.filters,
    state => getFlowItemsArray(state),
    state => getFlowFiltersArray(state),
    state => getFlowCasesArray(state),
    state => state.selected.flow,
    state => state.fields.byId,
    (flowRelations, filters, flowItems, filterArray, caseArray, flowId, fieldsById) =>
        getAncestorFieldsForFlowItems(flowRelations, filters, flowItems, filterArray, caseArray, flowId, fieldsById)
);

export function getAncestorsFlowItemIds(flowRelations, flowItemId, results) {
    const parentFlowItemIds = flowRelations
        // adding a little check to prevent running multiple times for the same parent, happens on big flows and affects performance
        .filter(
            x => x.ParentFlowItemId != 0 && x.ChildFlowItemId == flowItemId && !results.includes(x.ParentFlowItemId)
        )
        .map(x => x.ParentFlowItemId);

    for (const parentId of parentFlowItemIds) {
        results.push(parentId);
        getAncestorsFlowItemIds(flowRelations, parentId, results);
    }
}

// Re-export validation rule arrays for external usage (for legacy and compatibility)
export {
    cannotHaveChild,
    cannotHaveParent,
    childMustBeEmpty,
    allowsMultipleParents,
} from "./flowRelationValidationRules";
